import { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Plus, Edit2, Trash2, Eye, EyeOff, UserCheck, UserX } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULE_GROUPS = [
    {
        label: 'General',
        modules: [{ slug: 'dashboard', label: 'Dashboard' }]
    },
    {
        label: 'Producción',
        modules: [
            { slug: 'lotes', label: 'Lotes' },
            { slug: 'cortes', label: 'Cortes' },
            { slug: 'maquila', label: 'Maquila' },
            { slug: 'lavanderia', label: 'Lavandería' },
            { slug: 'habilitacion', label: 'Habilitación' },
        ]
    },
    {
        label: 'Inventarios',
        modules: [
            { slug: 'producto_terminado', label: 'Producto Terminado' },
            { slug: 'telas', label: 'Telas' },
            { slug: 'cierres', label: 'Cierres' },
            { slug: 'botones', label: 'Botones' },
            { slug: 'bolsas', label: 'Bolsas' },
            { slug: 'cintas', label: 'Cintas' },
            { slug: 'playeras', label: 'Playeras' },
        ]
    },
    {
        label: 'Ventas y Clientes',
        modules: [
            { slug: 'ventas', label: 'Punto de Venta' },
            { slug: 'clientes', label: 'Clientes' },
        ]
    },
    {
        label: 'Finanzas',
        modules: [
            { slug: 'proveedores', label: 'Proveedores' },
            { slug: 'cuentas_por_pagar', label: 'Cuentas por Pagar' },
        ]
    },
    {
        label: 'Personal',
        modules: [
            { slug: 'empleados', label: 'Empleados' },
            { slug: 'asistencia', label: 'Asistencia' },
            { slug: 'nomina', label: 'Nómina' },
        ]
    },
    {
        label: 'Tienda',
        modules: [
            { slug: 'productos', label: 'Productos' },
            { slug: 'pedidos', label: 'Pedidos' },
        ]
    },
    {
        label: 'Sistema',
        modules: [
            { slug: 'catalogos', label: 'Catálogos' },
            { slug: 'reportes', label: 'Reportes' },
        ]
    }
];

const ALL_MODULE_SLUGS = MODULE_GROUPS.flatMap(g => g.modules.map(m => m.slug));

const initialForm = {
    username: '',
    password: '',
    nombre: '',
    rol: 'empleado',
    permisos: ['dashboard']  // dashboard siempre obligatorio para evitar loop de redirect
};

export const Usuarios = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => { fetchUsers(); }, []);

    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/auth/users`);
            setUsers(res.data);
        } catch (e) {
            toast.error('Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.username || !form.nombre) {
            toast.error('Complete usuario y nombre');
            return;
        }
        if (!editingUser && !form.password) {
            toast.error('La contraseña es obligatoria');
            return;
        }

        try {
            if (editingUser) {
                const update = { nombre: form.nombre, rol: form.rol, permisos: form.permisos };
                if (form.password) update.password = form.password;
                await axios.put(`${API_URL}/api/auth/users/${editingUser.id}`, update);
                toast.success('Usuario actualizado');
            } else {
                await axios.post(`${API_URL}/api/auth/register`, form);
                toast.success('Usuario creado');
            }
            setModalOpen(false);
            setForm(initialForm);
            setEditingUser(null);
            fetchUsers();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al guardar');
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setForm({
            username: user.username,
            password: '',
            nombre: user.nombre,
            rol: user.rol,
            permisos: user.permisos || []
        });
        setModalOpen(true);
    };

    const handleToggleActive = async (user) => {
        try {
            await axios.put(`${API_URL}/api/auth/users/${user.id}`, { activo: !user.activo });
            toast.success(user.activo ? 'Usuario desactivado' : 'Usuario reactivado');
            fetchUsers();
        } catch (e) {
            toast.error('Error al cambiar estado');
        }
    };

    const togglePermiso = (slug) => {
        setForm(prev => ({
            ...prev,
            permisos: prev.permisos.includes(slug)
                ? prev.permisos.filter(p => p !== slug)
                : [...prev.permisos, slug]
        }));
    };

    const selectAll = () => setForm(prev => ({ ...prev, permisos: [...ALL_MODULE_SLUGS] }));
    const deselectAll = () => setForm(prev => ({ ...prev, permisos: ['dashboard'] }));

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="usuarios-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100">Gestión de Usuarios</h1>
                    <p className="text-zinc-400 mt-1">Crea cuentas y asigna permisos de acceso</p>
                </div>
                <Button onClick={() => { setForm(initialForm); setEditingUser(null); setModalOpen(true); }}
                    className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="add-user-btn">
                    <Plus className="w-5 h-5 mr-2" /> Nuevo Usuario
                </Button>
            </div>

            <Card className="card">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-zinc-800/50">
                                <TableHead className="table-header">Usuario</TableHead>
                                <TableHead className="table-header">Nombre</TableHead>
                                <TableHead className="table-header">Rol</TableHead>
                                <TableHead className="table-header">Permisos</TableHead>
                                <TableHead className="table-header">Estado</TableHead>
                                <TableHead className="table-header text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users.map(u => (
                                <TableRow key={u.id} className="table-row" data-testid={`user-row-${u.id}`}>
                                    <TableCell className="table-cell font-medium">
                                        <div className="flex items-center gap-2">
                                            <Shield className="w-4 h-4 text-amber-400" />
                                            {u.username}
                                        </div>
                                    </TableCell>
                                    <TableCell className="table-cell">{u.nombre}</TableCell>
                                    <TableCell className="table-cell">
                                        <span className={u.rol === 'administrador' ? 'badge-warning' : 'badge-info'}>
                                            {u.rol === 'administrador' ? 'Admin' : 'Empleado'}
                                        </span>
                                    </TableCell>
                                    <TableCell className="table-cell">
                                        {u.rol === 'administrador' ? (
                                            <span className="text-xs text-zinc-500">Acceso total</span>
                                        ) : (
                                            <span className="text-xs text-zinc-400">{(u.permisos || []).length} módulos</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="table-cell">
                                        <span className={u.activo !== false ? 'badge-success' : 'badge-error'}>
                                            {u.activo !== false ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </TableCell>
                                    <TableCell className="table-cell text-right">
                                        <div className="flex justify-end gap-1">
                                            <Button variant="ghost" size="sm" onClick={() => handleEdit(u)} data-testid={`edit-user-${u.id}`}>
                                                <Edit2 className="w-4 h-4 text-amber-400" />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleToggleActive(u)} data-testid={`toggle-user-${u.id}`}>
                                                {u.activo !== false
                                                    ? <UserX className="w-4 h-4 text-red-400" />
                                                    : <UserCheck className="w-4 h-4 text-emerald-400" />
                                                }
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto" data-testid="user-modal">
                    <DialogHeader>
                        <DialogTitle>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Usuario *</Label>
                                <Input value={form.username} disabled={!!editingUser}
                                    onChange={e => setForm({ ...form, username: e.target.value })}
                                    placeholder="usuario" data-testid="input-username" />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">{editingUser ? 'Nueva Contraseña' : 'Contraseña *'}</Label>
                                <div className="relative">
                                    <Input type={showPassword ? 'text' : 'password'} value={form.password}
                                        onChange={e => setForm({ ...form, password: e.target.value })}
                                        placeholder={editingUser ? '(sin cambio)' : 'contraseña'}
                                        data-testid="input-password" />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Nombre Completo *</Label>
                                <Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
                                    placeholder="Nombre" data-testid="input-nombre" />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Rol *</Label>
                                <Select value={form.rol} onValueChange={v => setForm({ ...form, rol: v, permisos: v === 'administrador' ? ALL_MODULE_SLUGS : form.permisos })}>
                                    <SelectTrigger data-testid="select-rol"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="administrador">Administrador</SelectItem>
                                        <SelectItem value="empleado">Empleado</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Permissions section */}
                        {form.rol === 'empleado' && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <Label className="form-label">Permisos de Acceso</Label>
                                    <div className="flex gap-2">
                                        <button type="button" onClick={selectAll} className="text-xs text-amber-400 hover:underline">Todos</button>
                                        <button type="button" onClick={deselectAll} className="text-xs text-zinc-500 hover:underline">Ninguno</button>
                                    </div>
                                </div>
                                <div className="rounded-lg border border-zinc-700 bg-zinc-800/50 p-3 space-y-3 max-h-[300px] overflow-y-auto">
                                    {MODULE_GROUPS.map(group => (
                                        <div key={group.label}>
                                            <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">{group.label}</p>
                                            <div className="grid grid-cols-2 gap-1.5">
                                                {group.modules.map(mod => {
                                                    const isLocked = mod.slug === 'dashboard';
                                                    return (
                                                    <label key={mod.slug}
                                                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-colors text-sm ${
                                                            form.permisos.includes(mod.slug)
                                                                ? 'bg-amber-400/10 text-amber-300'
                                                                : 'text-zinc-400 hover:bg-zinc-700/50'
                                                        } ${isLocked ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'}`}
                                                        title={isLocked ? 'Dashboard es obligatorio (evita pantalla negra)' : ''}
                                                        data-testid={`perm-${mod.slug}`}>
                                                        <Checkbox
                                                            checked={form.permisos.includes(mod.slug)}
                                                            disabled={isLocked}
                                                            onCheckedChange={() => !isLocked && togglePermiso(mod.slug)}
                                                        />
                                                        {mod.label}{isLocked && ' 🔒'}
                                                    </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-xs text-zinc-500">{form.permisos.length} de {ALL_MODULE_SLUGS.length} módulos seleccionados</p>
                            </div>
                        )}

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
                            <Button type="submit" className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="save-user-btn">
                                {editingUser ? 'Actualizar' : 'Crear Usuario'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
